// WHICH REGISTERED TABLET THIS IS (spec 04 §5 r15 amendment).
//
// Extracted from station-home.tsx at UX-5's surface remediation, because the
// op screen needs the same control and had a LINK to this one instead.
//
// The failure it fixes (surface review finding 1). `resolveDevicePin` answers
// `undefined` whenever the facility has MORE THAN ONE registered device and
// this browser has not yet pinned one — the ordinary state of a plant with two
// stations, reached by a `/q/op/` scan, a deep link, a bookmarked op or a
// browser restart. Every tap on the op screen is then withheld (correctly: a
// guessed device id stamps another tablet's identity on the ledger), but the
// screen's only account of it was a 15 px hint near the top with a link to the
// station screen. An operator who keys 420 into the numpad, picks a scrap
// reason and scrolls 580 px to Report finds a dead button labelled "Report",
// with the explanation off-screen behind them. The work is typed and lost.
//
// The control belongs where the work is. Rendering the picker inline means the
// answer is one tap away on the screen that is blocked, instead of a
// navigation that discards the form.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { DevicePin, StationSnapshotDevice } from "../lib/production-floor";

export function DeviceIdentity({
  devices,
  pick,
  onPick,
}: {
  devices: readonly StationSnapshotDevice[];
  pick: DevicePin | undefined;
  onPick: (pin: DevicePin) => void;
}) {
  const { t } = useTranslation();
  const [changing, setChanging] = useState(false);

  if (devices.length === 0) {
    // No registered device for this facility: e13 will 404 the batch, so the
    // taps are not offered a false path — the office registers devices (S8).
    return (
      <p className="floor-hint floor-hint-warn" data-testid="station-no-device">
        {t("production.floor.station.noDevices")}
      </p>
    );
  }

  const current = pick !== undefined && devices.some((d) => d.id === pick.id) ? pick : undefined;

  if (current !== undefined && !changing) {
    return (
      <p className="station-device" data-testid="station-device">
        {t("production.floor.station.device", { code: current.code, name: current.name })}
        {devices.length > 1 ? (
          <button
            type="button"
            className="floor-btn floor-btn-ghost"
            onClick={() => {
              setChanging(true);
            }}
          >
            {t("production.floor.station.changeDevice")}
          </button>
        ) : null}
      </p>
    );
  }

  return (
    <section className="floor-panel" data-testid="station-device-picker">
      <h2 className="floor-panel-head">{t("production.floor.station.pickDevice")}</h2>
      <p className="floor-hint">{t("production.floor.station.pickDeviceBody")}</p>
      <ul className="floor-cards">
        {devices.map((device) => (
          <li key={device.id} className="floor-card">
            <p className="floor-card-title">{device.device_code}</p>
            <p className="floor-card-sub">{device.name}</p>
            <button
              type="button"
              className="floor-btn floor-btn-primary"
              data-testid="station-device-pick"
              onClick={() => {
                onPick({ id: device.id, code: device.device_code, name: device.name });
                setChanging(false);
              }}
            >
              {t("production.floor.station.thisDevice")}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
